import { useEffect, useState } from "react";
import { Route, Loader2, AlertTriangle, Hand, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PlanRouteCard } from "@/components/PlanRouteCard";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";

export const PlanPanel = ({ date, onPlanned }) => {
  const [plan, setPlan] = useState(null);
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/deliveries/plan", { params: { date } }).then((r) => setPlan(r.data)).catch(() => {}); }, [date]);
  useEffect(() => { api.get("/users", { params: { role: "delivery" } }).then((r) => setPartners(r.data)).catch(() => {}); }, []);
  const run = async () => {
    if (plan?.manual && !window.confirm("Re-planning will undo your manual moves for this date. Continue?")) return;
    setBusy(true);
    try { const r = await api.post("/deliveries/plan", null, { params: { date } }); setPlan(r.data); toast.success(`Planned ${r.data.orders} deliveries`); onPlanned(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const move = async (body) => {
    try { const r = await api.post("/deliveries/plan/move", { ...body, date }); setPlan(r.data.plan); toast.success(`Moved ${r.data.moved} drops`); onPlanned(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const notify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/deliveries/plan/notify", null, { params: { date } });
      setPlan({ ...plan, changed_partners: r.data.pending });
      toast.success(`WhatsApp sent to ${r.data.sent} partner(s)`);
      if (r.data.no_phone.length) toast.warning(`No WhatsApp number for ${r.data.no_phone.join(", ")}. Add it in Team.`);
      if (r.data.failed) toast.error(`${r.data.failed} message(s) failed`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const testAlert = async (partner_id) => {
    try { const r = await api.post("/deliveries/plan/notify-test", { partner_id, date }); toast.success(`Test alert sent to ${r.data.to}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changed = plan?.changed_partners || [];
  const routes = plan?.routes || [];
  const used = new Set(routes.map((r) => r.partner_id).filter(Boolean));
  const cards = plan?.routes ? [...routes, ...partners.filter((p) => !used.has(p.id)).map((p) => ({ partner: p.name, partner_id: p.id, count: 0, km: 0, societies: [] }))] : [];
  if (cards.length && !cards.some((r) => !r.partner_id)) cards.push({ partner: null, partner_id: null, count: 0, km: 0, societies: [] });
  const targets = [...partners.map((p) => ({ id: p.id, name: p.name })), { id: null, name: "Unassigned" }];
  return (
    <div className="mb-6 rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="plan-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-display text-lg font-semibold text-[#0F382C]"><Route className="h-5 w-5 text-[#D96B43]" />Smart delivery plan · {fmtDate(date)}</div>
          <div className="text-xs text-muted-foreground">{plan?.created_at ? `Last planned ${fmtDateTime(plan.created_at)} by ${plan.by} · max ${plan.capacity} per partner` : "Auto-runs at 8:15 PM for tomorrow. Run any time after changes."}</div>
          {plan?.manual && <div className="mt-1 flex items-center gap-1 text-xs font-medium text-[#D96B43]" data-testid="plan-manual-badge"><Hand className="h-3 w-3" />Edited by {plan.edited_by} {fmtDateTime(plan.edited_at)} · 8:15 PM auto-plan will keep your changes</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {changed.length > 0 && <Button disabled={busy} variant="outline" onClick={notify} className="rounded-full border-[#D96B43] text-[#D96B43] hover:bg-[#D96B43]/10" data-testid="notify-partners-btn"><Send className="mr-1 h-4 w-4" />Notify {changed.length} changed partner{changed.length > 1 ? "s" : ""}</Button>}
          <Button disabled={busy} onClick={run} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="run-plan-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Plan deliveries"}</Button>
        </div>
      </div>
      {plan?.overflow > 0 && <div className="mt-3 flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="plan-overflow"><AlertTriangle className="h-4 w-4" />{plan.overflow} orders unassigned. Drag them to a partner or add more delivery partners.</div>}
      {cards.length > 0 && <>
        <div className="mt-4 text-xs text-muted-foreground">Drag a society or tower chip onto another partner, or use the arrow on a chip.</div>
        <div className="mt-2 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="plan-routes">
          {cards.map((r, i) => <PlanRouteCard key={r.partner_id || `none-${i}`} route={r} idx={i} cap={plan.capacity} targets={targets} onMove={move} vehicle={partners.find((p) => p.id === r.partner_id)?.vehicle} onTest={testAlert} />)}
        </div>
      </>}
    </div>
  );
};
